import { green, lightBlue, red } from '@mui/material/colors';
import _ from 'lodash';

import { PendingChangeKind } from 'constants/pendingChanges';
import {
  COLOR_PALETTE,
  DEPRECATED_THEME_COLORS,
  ILLUSTRATION_FILLS,
  SCREEN_WIDTH,
} from 'constants/ui';
import { getRecalculatedValueStyles } from 'styles/pendingChanges.styles';
import { getConditionalObject } from 'utils/data';

import { getMuiButtonOverrides } from './overrides/muiButton';
import { muiOutlinedInputVariants } from './overrides/muiOutlinedInput';

const formattedColorPaletteKeysMap = {
  brand: ['primary', 'info'],
  red: 'error',
  orange: 'warning',
  green: 'success',
};

const customBreakpoints = {
  values: {
    xs: 0,
    sm: SCREEN_WIDTH.S,
    md: SCREEN_WIDTH.M,
    lg: SCREEN_WIDTH.L,
    lgWide: SCREEN_WIDTH.L_WIDE,
    xl: SCREEN_WIDTH.XL,
  },
};

const themeColorPalette = Object.entries(COLOR_PALETTE).reduce((acc, [key, colorRange]) => {
  const colorKey = _.camelCase(key);
  const formattedColorKey = formattedColorPaletteKeysMap[colorKey] ?? colorKey;

  const main = colorRange[6] ?? colorRange[colorRange.length - 1];
  const soft = colorRange[2] ?? main;
  const light = colorRange[4] ?? main;
  const dark = colorRange[7] ?? main;
  const extraSoft = colorRange[1] ?? main;

  const colorVariants = {
    main,
    soft,
    light,
    dark,
    extraSoft,
  };

  if (Array.isArray(formattedColorKey)) {
    formattedColorKey.forEach(key => {
      acc[key] = colorVariants;
    });

    return acc;
  }

  acc[formattedColorKey] = colorVariants;

  return acc;
}, {});

const themesConfig = {
  default: {
    breakpoints: customBreakpoints,
    palette: {
      mode: 'light',
      primary: DEPRECATED_THEME_COLORS.DARK,
      secondary: {
        light: lightBlue[400],
        main: lightBlue[600],
        dark: lightBlue[700],
      },
      error: red,
      success: green,
      divider: 'rgba(0, 0, 0, 0.12)',
    },
    status: {
      danger: 'orange',
    },
  },
  coreDesignSystem: {
    breakpoints: customBreakpoints,
    themeName: 'cdsTheme',
    palette: {
      ...themeColorPalette,
      secondary: {
        light: '#1485E3',
        main: '#1485E3',
        dark: '#1485E3',
      },
      text: {
        primary: COLOR_PALETTE.GRAY[10],
        secondary: COLOR_PALETTE.GRAY[7],
        disabled: COLOR_PALETTE.GRAY[5],
        placeholder: COLOR_PALETTE.GRAY[6],
        dark: COLOR_PALETTE.GRAY[8],
        strong: COLOR_PALETTE.GRAY[9],
        highlight: COLOR_PALETTE.ADDITIONAL[2],
        tableHighlight: COLOR_PALETTE.ORANGE[8],
        error: COLOR_PALETTE.RED[7],
        link: COLOR_PALETTE.BLUE[7],
        // Text on an inverse-contrast surface (e.g. tooltip). Flips with the theme mode.
        inverse: COLOR_PALETTE.GRAY[1],
      },
      background: {
        primary: COLOR_PALETTE.GRAY[1],
        secondary: COLOR_PALETTE.GRAY[2],
        highlight: COLOR_PALETTE.ADDITIONAL[1],
        tableHighlight: COLOR_PALETTE.ORANGE[2],
        // Table header rows. Dark override: DARK_TABLE_HEADER in coreDesignSystemDark.
        tableHeader: COLOR_PALETTE.GRAY[2],
        hoverDefault: COLOR_PALETTE.GRAY[4],
        hoverHighlight: COLOR_PALETTE.ADDITIONAL[3],
        backdrop: 'rgba(0, 0, 0, 0.3)',
        ligthBackdrop: 'rgba(0, 0, 0, 0.03)',
        // Segmented control (SecondaryTabGroupCDS): the track and the selected segment lifted off it.
        segmentTrack: 'rgba(0, 0, 0, 0.03)',
        segmentSelected: COLOR_PALETTE.GRAY[1],
        // Disabled control surface (e.g. disabled input/radio).
        disabled: COLOR_PALETTE.GRAY[3],
        // Inverse-contrast surface (e.g. tooltip). Flips with the theme mode.
        inverse: COLOR_PALETTE.GRAY[10],
        // Backward-compatible aliases for legacy components using Main theme palette paths
        paper: COLOR_PALETTE.GRAY[1],
        default: COLOR_PALETTE.GRAY[1],
      },
      illustration: ILLUSTRATION_FILLS,
      border: {
        light: COLOR_PALETTE.GRAY[3],
        default: COLOR_PALETTE.GRAY[4],
        filters: COLOR_PALETTE.GRAY[5],
        input: COLOR_PALETTE.GRAY[5],
        inputHover: COLOR_PALETTE.GRAY[6],
        strong: COLOR_PALETTE.GRAY[9],
        success: COLOR_PALETTE.GREEN[5],
      },
      shadow: {
        s: '0px 1px 4px 0px rgba(18, 23, 29, 0.15)',
        m: '0px 3px 6px 0px rgba(18, 23, 29, 0.1)',
        l: '0px 9px 28px 0px rgba(0, 0, 0, 0.05)',
      },
      icon: {
        info: COLOR_PALETTE.BLUE[7],
        disabled: COLOR_PALETTE.GRAY[6],
      },
    },
    shape: {
      radius: {
        s: '4px',
        m: '6px',
        l: '8px',
        xl: '12px',
      },
    },
    transition: {
      default: 'all 0.2s ease-in-out',
    },
    typography: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: 14,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      regular12: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: '20px',
      },
      regularItalic12: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        fontStyle: 'italic',
        lineHeight: '20px',
      },
      medium12: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '20px',
      },
      mediumItalic12: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 500,
        fontStyle: 'italic',
        lineHeight: '20px',
      },
      bold12: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '20px',
      },
      boldItalic12: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 700,
        fontStyle: 'italic',
        lineHeight: '20px',
      },
      regular14: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '22px',
      },
      medium14: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '22px',
      },
      bold14: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 14,
        fontWeight: 700,
        lineHeight: '22px',
      },
      regular16: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '24px',
      },
      medium16: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '24px',
      },
      bold16: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 16,
        fontWeight: 700,
        lineHeight: '24px',
      },
      regular20: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 20,
        fontWeight: 400,
        lineHeight: '28px',
      },
      medium20: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 20,
        fontWeight: 500,
        lineHeight: '28px',
      },
      bold20: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 20,
        fontWeight: 700,
        lineHeight: '28px',
      },
      regular24: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 24,
        fontWeight: 400,
        lineHeight: '32px',
      },
      medium24: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 24,
        fontWeight: 500,
        lineHeight: '32px',
      },
      bold24: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 24,
        fontWeight: 700,
        lineHeight: '32px',
      },
      regular32: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 32,
        fontWeight: 400,
        lineHeight: '38px',
      },
      medium32: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 32,
        fontWeight: 500,
        lineHeight: '38px',
      },
      bold32: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 32,
        fontWeight: 700,
        lineHeight: '38px',
      },
      regular38: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 36,
        fontWeight: 400,
        lineHeight: '46px',
      },
      medium38: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 36,
        fontWeight: 500,
        lineHeight: '46px',
      },
      bold38: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 36,
        fontWeight: 700,
        lineHeight: '46px',
      },
      regular40: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 40,
        fontWeight: 400,
        lineHeight: '48px',
      },
      medium40: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 40,
        fontWeight: 500,
        lineHeight: '48px',
      },
      bold40: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 40,
        fontWeight: 700,
        lineHeight: '48px',
      },
      regular48: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 48,
        fontWeight: 400,
        lineHeight: '64px',
      },
      medium48: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 48,
        fontWeight: 500,
        lineHeight: '64px',
      },
      bold48: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 48,
        fontWeight: 700,
        lineHeight: '64px',
      },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...getConditionalObject(ownerState.isHidden, {
              display: 'none',
            }),
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '1.2rem',
            maxWidth: 'none',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme: { palette } }) => ({
            borderColor: palette.border.default,
          }),
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          color: 'primary',
          size: 'medium',
        },
        styleOverrides: {
          root: getMuiButtonOverrides,
          line: ({ theme: { palette } }) => ({
            border: `1px solid ${palette.border.input}`,
            color: palette.text.primary,
            backgroundColor: 'transparent',

            '&:hover': {
              backgroundColor: `${palette.background.secondary} !important`,
            },

            '&:focus, &:focus-visible, &.Mui-focusVisible': {
              boxShadow: `0px 0px 0px 2px ${palette.border.default}`,
            },

            '&.Mui-disabled': {
              color: palette.text.placeholder,
              // Dark: the root opacity:0.4 fades the ghost border + label to near-invisible. Keep
              // it legible (full opacity, dimmed-but-readable label, faint visible border). Light is
              // unchanged (placeholder color only).
              ...getConditionalObject(palette.mode === 'dark', () => ({
                opacity: 1,
                color: palette.text.disabled,
                border: `1px solid ${palette.border.light}`,
              })),
            },
          }),
          icon: {
            fontSize: '20px',
            '& > *:first-of-type': {
              fontSize: '20px',
            },
            '& svg': {
              width: '20px',
              height: '20px',
            },
          },
          startIcon: {
            marginRight: '4px',
          },
          endIcon: {
            marginLeft: '4px',
          },
          sizeSmall: ({ theme: { shape } }) => ({
            minHeight: '24px',
            padding: '1px 8px',
            borderRadius: shape.radius.m,
            fontSize: 14,
            lineHeight: 1.375,

            '& .MuiButton-icon': {
              fontSize: '16px',

              '& > *:first-of-type': {
                fontSize: '16px',
              },

              '& svg': {
                width: '16px',
                height: '16px',
              },
            },
          }),
          sizeMedium: {
            minHeight: '32px',
            padding: '3px 12px',

            '& .MuiButton-icon': {
              fontSize: '20px',

              '& > *:first-of-type': {
                fontSize: '20px',
              },

              '& svg': {
                width: '20px',
                height: '20px',
              },
            },
          },
          sizeLarge: {
            minHeight: '40px',
            padding: '7px 16px',

            '& .MuiButton-icon': {
              fontSize: '20px',

              '& > *:first-of-type': {
                fontSize: '20px',
              },

              '& svg': {
                width: '20px',
                height: '20px',
              },
            },
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
          size: 'medium',
        },
        styleOverrides: {
          root: ({ theme: { shape }, ownerState }) => ({
            padding: 0,
            borderRadius: shape.radius.l,
            ...getConditionalObject(ownerState.isHidden, {
              display: 'none',
            }),
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            position: 'relative',
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          size: 'large',
        },
        styleOverrides: {
          root: ({ theme: { palette, shape } }) => ({
            backgroundColor: palette.background.primary,
            border: `1px solid ${palette.border.input}`,
            borderRadius: shape.radius.l,

            '&:hover': {
              borderColor: palette.border.inputHover,
            },

            '&.Mui-focused': {
              borderColor: COLOR_PALETTE.BRAND[6],
              boxShadow: `0px 0px 0px 3px ${COLOR_PALETTE.BRAND[2]}`,
            },

            '&[data-is-changed="true"]:not(.Mui-error)': {
              borderColor: COLOR_PALETTE.ORANGE[7],

              '&:hover:not(.Mui-disabled)': {
                borderColor: COLOR_PALETTE.ORANGE[6],
              },

              '&.Mui-focused': {
                borderColor: COLOR_PALETTE.ORANGE[7],
                boxShadow: `0px 0px 0px 3px ${COLOR_PALETTE.ORANGE[2]}`,
              },
            },

            // An input clips its text box, so an underline offset past ~1px is never painted.
            [`&[data-change-kind="${PendingChangeKind.RECALCULATED}"] input, &[data-change-kind="${PendingChangeKind.RECALCULATED}"] textarea`]:
              getRecalculatedValueStyles({
                decorationColor: palette.text.secondary,
                underlineOffset: '1px',
              }),

            '&.Mui-error': {
              borderColor: palette.text.error,

              '&:hover': {
                borderColor: COLOR_PALETTE.RED[6],
              },

              '&.Mui-focused': {
                borderColor: palette.text.error,
                boxShadow: `0px 0px 0px 3px ${COLOR_PALETTE.RED[2]}`,
              },
            },

            '&.Mui-disabled': {
              backgroundColor: palette.background.disabled,

              '&:not(.Mui-error):not([data-is-changed="true"])': {
                borderColor: palette.border.input,
              },
            },

            '& fieldset': {
              border: 'unset',
            },

            '& input': {
              position: 'relative',
              boxSizing: 'border-box',
              padding: '9px 12px !important',
              height: '100%',
            },

            '& textarea': {
              boxSizing: 'border-box',
              padding: '9px 12px !important',
            },

            '&.MuiAutocomplete-inputRoot': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: '0 !important',
            },

            '&.MuiAutocomplete-inputRoot input': {
              height: 'auto',
            },

            '& input, & textarea': {
              color: `${palette.text.primary} !important`,
              caretColor: palette.text.primary,

              '&:-webkit-autofill': {
                boxShadow: 'unset !important',
                textFillColor: 'unset',
                color: `${palette.text.primary} !important`,
                caretColor: palette.text.primary,
              },

              '&:disabled': {
                WebkitTextFillColor: 'unset !important',
              },

              '&:placeholder': {
                color: `${palette.text.placeholder} !important`,
              },
            },

            variants: muiOutlinedInputVariants,
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            lineHeight: 1,
          },
          outlined: {
            '&.MuiInputLabel-shrink': {
              transform: 'translate(0, -20px)',
              marginBottom: '4px',
            },
          },
        },
      },
      MuiSwitch: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
          size: 'large',
        },
        styleOverrides: {
          root: ({ ownerState }) => ({
            width: '34px',
            height: '20px',
            padding: 0,
            ...getConditionalObject(ownerState.isHidden, {
              display: 'none',
            }),
          }),
          switchBase: {
            padding: '2px',

            '&.Mui-checked': {
              transform: 'translateX(14px)',
            },

            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
          track: ({ theme: { palette } }) => ({
            borderRadius: 10,
            backgroundColor: palette.border.inputHover,
            opacity: 1,

            '.Mui-disabled + &': {
              backgroundColor: palette.border.default,
              opacity: '1 !important',
            },

            '.Mui-disabled.Mui-checked + &': {
              backgroundColor: COLOR_PALETTE.BRAND[3],
            },

            '.Mui-checked + &': {
              backgroundColor: palette.primary.main,
              opacity: '1 !important',
            },
          }),
          thumb: ({ theme: { palette } }) => ({
            boxSizing: 'border-box',
            width: '16px',
            height: '16px',
            backgroundColor: palette.background.primary,
            boxShadow: palette.shadow.s,

            '.Mui-checked + &': {
              backgroundColor: palette.background.primary,
            },
          }),
          sizeSmall: {
            width: '30px',
            height: '16px',

            '& .MuiSwitch-thumb': {
              width: '12px',
              height: '12px',
            },

            '& .MuiSwitch-switchBase': {
              padding: '2px',

              '&.Mui-checked': {
                transform: 'translateX(14px)',
              },
            },
          },
          sizeLarge: {},
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme: { palette } }) => ({
            color: `${palette.text.disabled} !important`,

            '&.MuiCheckbox-indeterminate': {
              width: '20px',
              height: '20px',
            },

            '&.Mui-checked': {
              color: `${COLOR_PALETTE.BRAND[6]} !important`,
            },

            '&.Mui-disabled': {
              color: `${palette.text.disabled} !important`,
            },

            '&:hover': {
              color: `${COLOR_PALETTE.BRAND[6]} !important`,
            },
          }),
        },
      },
      MuiRadio: {
        defaultProps: {
          disableRipple: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: ({ theme: { palette } }) => ({
            padding: '7px',
            color: palette.text.disabled,

            '&.Mui-checked': {
              color: COLOR_PALETTE.BRAND[6],
            },

            '&.Mui-disabled': {
              color: palette.text.disabled,

              '& .MuiSvgIcon-root:first-of-type': {
                backgroundColor: palette.background.disabled,
                borderRadius: '50%',
                backgroundClip: 'content-box',
                padding: '2px',
              },

              '& .MuiSvgIcon-root:last-of-type': {
                backgroundColor: 'transparent',
              },
            },

            '&:hover': {
              color: COLOR_PALETTE.BRAND[6],
            },
          }),
        },
      },
      MuiRadioGroup: {
        styleOverrides: {
          root: {
            rowGap: '8px',
            columnGap: '16px',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            gap: '1px',

            '&:has(.MuiSwitch-root)': {
              gap: '8px',
              marginLeft: 0,
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: '0.8rem',
            boxShadow:
              '0px 5px 12px 4px rgba(18, 23, 29, 0.05), 0px 3px 6px 0px rgba(18, 23, 29, 0.1), 0px 1px 2px -2px rgba(18, 23, 29, 0.1)',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme: { shape } }) => ({
            borderRadius: `${shape.radius.m} !important`,

            '&:before': {
              display: 'none',
            },
          }),
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            paddingInline: 0,
            minHeight: 'unset',
            '&.Mui-expanded': {
              minHeight: 'unset',
            },
          },
          content: {
            margin: 0,
            '&.Mui-expanded': {
              marginTop: 0,
            },
          },
          expandIconWrapper: ({ theme: { palette } }) => ({
            color: palette.text.dark,
          }),
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            paddingInline: 0,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
          },
          indicator: {
            backgroundColor: COLOR_PALETTE.BRAND[6],
            height: 2,
          },
        },
      },
      MuiTab: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: ({ theme: { palette } }) => ({
            minHeight: 40,
            padding: '0px 16px 12px',
            textTransform: 'none',
            color: palette.text.secondary,

            '&:hover': {
              color: palette.text.primary,
            },

            '&.Mui-selected': {
              color: palette.text.primary,
            },

            '&.Mui-disabled': {
              color: palette.text.disabled,
            },
          }),
        },
      },
      MuiTooltip: {
        defaultProps: {
          PopperProps: {
            disablePortal: true,
          },
        },
        styleOverrides: {
          tooltip: ({ theme: { palette, shape } }) => ({
            backgroundColor: palette.background.inverse,
            color: palette.text.inverse,
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '20px',
            padding: '4px 8px',
            borderRadius: shape.radius.m,
            boxShadow: palette.shadow.m,
            maxWidth: '320px',
          }),
          arrow: ({ theme: { palette } }) => ({
            color: palette.background.inverse,
          }),
        },
      },
    },
  },
  mainThemeLight: {
    breakpoints: customBreakpoints,
    palette: {
      mode: 'light',
      primary: {
        dark: '#3E83DC',
        main: '#3E83DC',
      },
      secondary: {
        light: '#1485E3',
        main: '#1485E3',
        dark: '#1485E3',
      },
      background: {
        primary: '#E55B5B',
        paper: COLOR_PALETTE.GRAY[1],
        default: COLOR_PALETTE.GRAY[1],
      },
      error: red,
      success: green,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: '1.2rem',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '1.2rem',

            '&:hover': {
              backgroundColor: 'rgba(20, 133, 227, 0.4) !important',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'rgba(20, 133, 227, 0.4) !important',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'rgba(20, 133, 227, 0.4) !important',
            },
          },
        },
      },
    },
  },
  defaultDark: {
    breakpoints: customBreakpoints,
    palette: {
      mode: 'dark',
      primary: DEPRECATED_THEME_COLORS.DARK,
      secondary: {
        light: DEPRECATED_THEME_COLORS.SKY_BLUE[100],
        main: DEPRECATED_THEME_COLORS.SKY_BLUE[500],
        dark: DEPRECATED_THEME_COLORS.SKY_BLUE[900],
      },
      background: {
        primary: '#E55B5B',
        paper: '#1E2125',
        default: '#121212',
      },
      error: red,
      success: green,
    },
    status: {
      danger: 'orange',
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          stickyHeader: {
            backgroundColor: '#1E2125',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: 'white',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
          },
          input: {
            '&:-webkit-autofill': {
              '-webkit-transition-delay': '604800s',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '1.2rem',
          },
          textPrimary: {
            color: 'rgba(0, 0, 0, 0.87)',
            backgroundColor: '#e0e0e0',
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearSelected: {
            color: `${DEPRECATED_THEME_COLORS.DARK.A100} !important`,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          daySelected: {
            color: DEPRECATED_THEME_COLORS.DARK['500'],
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
          },
          current: {
            color: 'white',
            backgroundColor: DEPRECATED_THEME_COLORS.DARK['500'],
          },
        },
      },
    },
  },
};

// Dark CDS theme: inherits the full coreDesignSystem (typography, shape, brand colors, and the
// now-token-driven component overrides) and flips only the surface/text/border/shadow tokens. The
// shared overrides read these via palette callbacks, so converting them above is what makes dark
// render correctly without a parallel component-override set.
// Dark CDS theme tokens — authoritative values from the dark-mode design handoff ("Charcoal"
// default). Cards separate by SURFACE ELEVATION (bg #0E1117 < panel #181F2A < inner #1C2433)
// plus subtle white-alpha borders, not heavy lines.
const DARK_BG = '#0E1117'; // app background
const DARK_PANEL = '#181F2A'; // card / panel surface (lifted off the page for clearer separation)
const DARK_PANEL2 = '#1C2433'; // inner surface, inputs, hover rows
const DARK_TABLE_HEADER = '#1A212D'; // table header rows
const DARK_SIDEBAR = '#12171F'; // section sidebars (one notch darker than panel)
const DARK_SEGMENT_SELECTED = '#2A3446'; // selected segment of a segmented control
const DARK_ACCENT = '#3D8BFF';

themesConfig.coreDesignSystemDark = _.merge({}, themesConfig.coreDesignSystem, {
  themeName: 'cdsThemeDark',
  palette: {
    mode: 'dark',
    // MUI's own default (3:1) is below WCAG AA for text (4.5:1). Every contained-color button label
    // is painted via palette.getContrastText(main) (see overrides/muiButton.js); on the dark accent
    // fills (e.g. primary DARK_ACCENT #3D8BFF vs white text = 3.31:1) the 3:1 default picks white and
    // fails AA, while black on the same fill measures 6.33:1. Raising the threshold to the AA minimum
    // makes getContrastText resolve correctly everywhere it's used in dark mode, not just for buttons.
    contrastThreshold: 4.5,
    // extraSoft = the "selected/active control fill" tint (e.g. ToolbarButtonGroupCDS Mui-selected).
    // Without a dark value it inherits the light pale BRAND[1]/etc. and leaks near-white on dark.
    primary: {
      main: DARK_ACCENT,
      light: '#5AA0FF',
      dark: '#1F6BE0',
      soft: 'rgba(61,139,255,0.14)',
      extraSoft: 'rgba(61,139,255,0.18)',
    },
    // secondary inherits the light #1485E3 blue otherwise — a second, duller "primary" blue that
    // competes with the accent on dark (FAB add-buttons, color="secondary" controls). Align it to
    // the accent so every primary-intent blue reads as one deliberate color in dark.
    secondary: {
      main: DARK_ACCENT,
      light: '#5AA0FF',
      dark: '#1F6BE0',
      soft: 'rgba(61,139,255,0.14)',
      extraSoft: 'rgba(61,139,255,0.18)',
    },
    info: {
      main: DARK_ACCENT,
      light: '#5AA0FF',
      dark: '#1F6BE0',
      soft: 'rgba(61,139,255,0.14)',
      extraSoft: 'rgba(61,139,255,0.18)',
    },
    success: {
      main: '#2ED3A0',
      light: '#34E0AC',
      dark: '#15A87C',
      soft: 'rgba(46,211,160,0.13)',
      extraSoft: 'rgba(46,211,160,0.16)',
    },
    error: {
      main: '#F8788A',
      light: '#F8788A',
      dark: '#E25767',
      soft: 'rgba(248,120,138,0.13)',
      extraSoft: 'rgba(248,120,138,0.16)',
    },
    warning: {
      main: '#FBBF24',
      light: '#FBBF24',
      dark: '#D99E16',
      soft: 'rgba(251,191,36,0.14)',
      extraSoft: 'rgba(251,191,36,0.16)',
    },
    // Custom accent groups (from COLOR_PALETTE.PURPLE/CYAN) — no dark value otherwise, so their
    // light pale soft/extraSoft fills (#EBE9FE / #B5F5EC) leak near-white on dark (AI process chips,
    // ToolRow/Flow tints, AiBatchIntake). dark = the bright text variant used on the soft chip fill.
    purple: {
      main: '#9B8AFB',
      light: '#C4B5FD',
      dark: '#B7A9FC',
      soft: 'rgba(155,138,251,0.16)',
      extraSoft: 'rgba(155,138,251,0.12)',
    },
    cyan: {
      main: '#36CFC9',
      light: '#5CDBD3',
      dark: '#5CDBD3',
      soft: 'rgba(54,207,201,0.16)',
      extraSoft: 'rgba(54,207,201,0.12)',
    },
    // COLOR_PALETTE.BLUE (auto-derived into palette.blue via themeColorPalette) has no dark value
    // otherwise, so its light pale soft/extraSoft fill (#D1E9FF/#EFF8FF — e.g. section-heading info-icon
    // badges across Property & Loan) leaks near-white on dark. Aligned to the same accent blue as
    // primary/secondary/info so every "info" tint reads as one deliberate color in dark.
    blue: {
      main: DARK_ACCENT,
      light: '#5AA0FF',
      dark: '#1F6BE0',
      soft: 'rgba(61,139,255,0.14)',
      extraSoft: 'rgba(61,139,255,0.18)',
    },
    text: {
      primary: 'rgba(255,255,255,0.92)',
      secondary: 'rgba(255,255,255,0.56)',
      disabled: 'rgba(255,255,255,0.36)',
      placeholder: 'rgba(255,255,255,0.36)',
      dark: 'rgba(255,255,255,0.92)',
      strong: 'rgba(255,255,255,0.92)',
      link: DARK_ACCENT,
      error: '#F8788A',
      inverse: DARK_BG,
      highlight: 'rgba(255,255,255,0.92)',
      tableHighlight: 'rgba(255,255,255,0.92)',
    },
    background: {
      primary: DARK_BG,
      default: DARK_BG,
      paper: DARK_PANEL,
      secondary: DARK_PANEL2,
      hoverDefault: DARK_PANEL2,
      disabled: DARK_PANEL2,
      tableHeader: DARK_TABLE_HEADER,
      sidebar: DARK_SIDEBAR,
      backdrop: 'rgba(0,0,0,0.6)',
      inverse: COLOR_PALETTE.GRAY[3],
      // Highlighted / selected / added-modified rows + search-match cells: low-alpha accent tint on
      // the dark canvas (the light palette's pale ADDITIONAL/ORANGE tints leak as near-white here).
      highlight: 'rgba(61,139,255,0.14)',
      tableHighlight: 'rgba(61,139,255,0.14)',
      hoverHighlight: 'rgba(61,139,255,0.22)',
      ligthBackdrop: 'rgba(255,255,255,0.04)',
      // The light track (3% black) and selected segment (paper) collapse to near-identical charcoal
      // here, so the track is a faint white wash (visible on page and panel alike) and the selected
      // segment is an opaque surface lifted well above it.
      segmentTrack: 'rgba(255,255,255,0.06)',
      segmentSelected: DARK_SEGMENT_SELECTED,
    },
    // Low-alpha white keeps the empty-state outline legible without the pale light fills glaring.
    illustration: {
      shadow: 'rgba(255,255,255,0.03)',
      surface: DARK_PANEL2,
      inner: 'rgba(255,255,255,0.06)',
      stroke: 'rgba(255,255,255,0.22)',
    },
    border: {
      // White-alpha borders; default is visible enough to delineate cards/panels against the page,
      // with inputs/filters/strong a touch stronger. Light dividers stay subtle for dense tables.
      light: 'rgba(255,255,255,0.08)',
      default: 'rgba(255,255,255,0.16)',
      filters: 'rgba(255,255,255,0.16)',
      input: 'rgba(255,255,255,0.14)',
      inputHover: 'rgba(255,255,255,0.24)',
      strong: 'rgba(255,255,255,0.18)',
    },
    divider: 'rgba(255,255,255,0.08)',
    icon: {
      info: DARK_ACCENT,
      disabled: 'rgba(255,255,255,0.36)',
    },
    shadow: {
      s: '0px 1px 4px 0px rgba(0,0,0,0.5)',
      m: '0px 3px 6px 0px rgba(0,0,0,0.5)',
      l: '0px 9px 28px 0px rgba(0,0,0,0.6)',
    },
  },
});

export default themesConfig;
